import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Search, ArrowRight, CheckCircle2, XCircle, Layers } from "lucide-react";
import { api } from "@/lib/api";
import { ProductCard } from "@/components/ProductCard";

export default function BuildYourSet() {
  const [categories, setCategories] = useState([]);
  const [form, setForm] = useState({ category_slug: "", year: "", manufacturer: "", card_set: "" });
  const [numbers, setNumbers] = useState("");
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => { api.get("/categories").then((r) => setCategories(r.data)).catch(() => {}); }, []);

  const submit = async (e) => {
    e.preventDefault();
    const card_numbers = numbers.split(/[\n,]+/).map((n) => n.trim()).filter(Boolean);
    setLoading(true);
    try {
      const { data } = await api.post("/setbuilder/match", { ...form, card_numbers });
      setResult(data);
    } finally { setLoading(false); }
  };

  const field = "bg-[color:var(--charcoal)] border border-[color:var(--line)] rounded-xl px-4 py-3.5 text-white outline-none focus:border-[color:var(--cyan)] transition-colors w-full";

  return (
    <div className="bg-ink min-h-screen">
      <div className="relative border-b border-[color:var(--line)] overflow-hidden noise">
        <div className="absolute inset-0 opacity-30" style={{ background: "radial-gradient(60% 100% at 20% 0%, rgba(36,224,255,0.25), transparent 60%)" }} />
        <div className="relative mx-auto max-w-[1400px] px-5 py-16">
          <p className="eyebrow mb-4 flex items-center gap-2"><Layers size={14} className="text-[color:var(--cyan)]" /> Stop Hunting One Card At A Time</p>
          <h1 className="h-display text-5xl sm:text-6xl text-chrome">BUILD YOUR SET</h1>
          <p className="text-[color:var(--chrome-2)] mt-5 max-w-xl">Tell us what you're missing. We'll search our inventory and return your matches instantly.</p>
        </div>
      </div>

      <div className="mx-auto max-w-[1400px] px-5 py-12 grid lg:grid-cols-5 gap-10">
        <form onSubmit={submit} className="lg:col-span-2 glass rounded-3xl p-8 h-fit lg:sticky lg:top-24">
          <div className="space-y-5">
            <div>
              <label className="text-[10px] font-tech tracking-widest text-[color:var(--chrome-3)] block mb-2">CATEGORY</label>
              <select data-testid="bys-category" className={field} value={form.category_slug} onChange={(e) => setForm({ ...form, category_slug: e.target.value })}>
                <option value="">Any category</option>
                {categories.map((c) => <option key={c.slug} value={c.slug}>{c.name}</option>)}
              </select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-[10px] font-tech tracking-widest text-[color:var(--chrome-3)] block mb-2">YEAR</label>
                <input data-testid="bys-year" className={field} placeholder="e.g. 2023" value={form.year} onChange={(e) => setForm({ ...form, year: e.target.value })} />
              </div>
              <div>
                <label className="text-[10px] font-tech tracking-widest text-[color:var(--chrome-3)] block mb-2">MANUFACTURER</label>
                <input data-testid="bys-manufacturer" className={field} placeholder="e.g. Topps" value={form.manufacturer} onChange={(e) => setForm({ ...form, manufacturer: e.target.value })} />
              </div>
            </div>
            <div>
              <label className="text-[10px] font-tech tracking-widest text-[color:var(--chrome-3)] block mb-2">SET</label>
              <input data-testid="bys-set" className={field} placeholder="e.g. Chrome" value={form.card_set} onChange={(e) => setForm({ ...form, card_set: e.target.value })} />
            </div>
            <div>
              <label className="text-[10px] font-tech tracking-widest text-[color:var(--chrome-3)] block mb-2">MISSING CARD NUMBERS</label>
              <textarea data-testid="bys-numbers" rows={4} className={field} placeholder="Enter numbers separated by commas or new lines&#10;e.g. 112, 004, 018" value={numbers} onChange={(e) => setNumbers(e.target.value)} />
              <p className="text-[11px] text-[color:var(--chrome-3)] mt-2">Uploadable missing-card lists coming soon.</p>
            </div>
            <button type="submit" data-testid="bys-submit" disabled={loading} className="btn-holo w-full rounded-xl py-4 text-sm inline-flex items-center justify-center gap-2 disabled:opacity-60">
              {loading ? "SEARCHING..." : <>FIND MY CARDS <Search size={16} /></>}
            </button>
          </div>
        </form>

        <div className="lg:col-span-3">
          {!result && (
            <div className="h-full min-h-[300px] rounded-3xl border border-dashed border-[color:var(--line)] flex flex-col items-center justify-center text-center p-10">
              <Layers size={40} className="text-[color:var(--chrome-3)] mb-4" />
              <p className="text-[color:var(--chrome-2)]">Enter the cards you're missing and hit <span className="text-white font-semibold">Find My Cards</span>.</p>
              <p className="text-sm text-[color:var(--chrome-3)] mt-2">Try year <span className="text-white">2023</span>, manufacturer <span className="text-white">Topps</span>, number <span className="text-white">112</span>.</p>
            </div>
          )}

          {result && (
            <div className="space-y-10" data-testid="bys-results">
              <div className="flex flex-wrap gap-4">
                <div className="flex items-center gap-2 rounded-xl border border-[color:var(--line)] px-5 py-3">
                  <CheckCircle2 className="text-[color:var(--cyan)]" size={18} />
                  <span className="text-white font-semibold">{result.matched_count}</span>
                  <span className="text-[color:var(--chrome-3)] text-sm">matched</span>
                </div>
                <div className="flex items-center gap-2 rounded-xl border border-[color:var(--line)] px-5 py-3">
                  <XCircle className="text-[color:var(--magenta)]" size={18} />
                  <span className="text-white font-semibold">{result.missing_count}</span>
                  <span className="text-[color:var(--chrome-3)] text-sm">not in stock</span>
                </div>
              </div>

              {result.matched.length > 0 && (
                <div>
                  <h3 className="eyebrow mb-5">In Stock — Ready To Add</h3>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                    {result.matched.map((m) => <ProductCard key={m.product.id} product={m.product} />)}
                  </div>
                </div>
              )}

              {result.missing.length > 0 && (
                <div>
                  <h3 className="eyebrow mb-5">Not Currently In Stock</h3>
                  <div className="flex flex-wrap gap-2">
                    {result.missing.map((n) => (
                      <span key={n} className="rounded-lg border border-[color:var(--line)] px-4 py-2 text-sm text-[color:var(--chrome-2)] font-tech">#{n}</span>
                    ))}
                  </div>
                  <p className="text-sm text-[color:var(--chrome-3)] mt-4">We'll be adding a want-list alert so you're notified the moment these arrive.</p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
